import type { ReactNode } from 'react';
import { Badge } from '@/components/Badge';
import { Icon } from '@/components/Icon';
import { Money } from '@/components/Money';
import { COUNTER_CURRENCY } from '@/features/counter/config';
import { money } from '@/lib/money';
import type { Cart } from './api';
import { cartIssueLabel } from './wording';
import { cardClasses } from '@/components/Card';
import { cn } from '@/lib/cn';

export { Notice } from '@/components/Notice';

/** Who put a line in the cart. Violet is reserved for the waiter's own additions. */
export function Attribution({ by }: { by: 'CUSTOMER' | 'WAITER' }) {
  return by === 'WAITER' ? (
    <Badge tone="waiter">Added by waiter</Badge>
  ) : (
    <Badge tone="neutral">Added by customer</Badge>
  );
}

/** A server amount in the branch currency. No arithmetic: the value is shown exactly as it was sent. */
export function Amount({ minor, className }: { minor: number; className?: string }) {
  return <Money value={money(minor, COUNTER_CURRENCY)} className={className} />;
}

/** A white panel on the canvas: the one surface the waiter's lists and totals sit on. */
export function Panel({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn(cardClasses({ padding: 'none' }), className)}>{children}</div>;
}

/** The one empty-cart treatment, the same at a table and at a seat. */
export function EmptyCart() {
  return (
    <Panel className="flex flex-col items-center gap-2 px-4 py-6 text-center">
      <span className="grid size-10 place-items-center rounded-full bg-surface text-muted">
        <Icon name="cart" size={20} />
      </span>
      <p className="text-base font-semibold text-ink">Nothing in the cart yet</p>
    </Panel>
  );
}

/**
 * A white card with a coloured bar on its left edge, for a row that needs the eye: a session that asked
 * for help (amber), an order ready to go (green). `tone` undefined draws a plain card.
 */
export const EDGE: Record<'attention' | 'success' | 'active', string> = {
  attention: 'bg-attention',
  success: 'bg-success',
  active: 'bg-active',
};

export function EdgeBar({ tone }: { tone?: keyof typeof EDGE }) {
  if (!tone) return null;
  return <span aria-hidden="true" className={cn('absolute inset-y-0 left-0 w-1.5', EDGE[tone])} />;
}

/**
 * The customer's cart as the backend reports it: names, quantities, choices and notes. Its subtotal and total
 * are shown by `CartTotals`; lines carry no amount of their own. Both sit on one `Panel`.
 */
export function CartLines({ cart, locationType }: { cart: Cart; locationType: string }) {
  return (
    <ul className="flex flex-col divide-y divide-line">
      {cart.items.map((line) => {
        const issue = cartIssueLabel(line.issue, locationType);
        return (
          <li key={line.cart_item_id} className="flex flex-col gap-1 px-4 py-3">
            <span className="flex items-start justify-between gap-3">
              <span className="min-w-0 text-base font-semibold break-words text-ink">
                {line.quantity}× {line.name ?? 'Item'}
              </span>
              <Attribution by={line.added_by} />
            </span>
            {line.modifiers.length > 0 && (
              <span className="text-sm break-words text-muted">{line.modifiers.join(', ')}</span>
            )}
            {line.notes && (
              <span className="text-sm break-words text-muted">Note: {line.notes}</span>
            )}
            {issue && <span className="text-sm font-medium text-attention-ink">{issue}</span>}
          </li>
        );
      })}
    </ul>
  );
}

/**
 * The draft cart's amounts, so the waiter can tell a customer what is in their cart. Both figures are the
 * backend's. This is a cart, not a bill: nothing here is owed until the customer places the order.
 */
export function CartTotals({ cart }: { cart: Cart }) {
  return (
    <dl
      aria-label="Cart amounts"
      className="flex flex-col gap-1 border-t border-line bg-surface/60 px-4 py-3 text-sm"
    >
      <div className="flex items-baseline justify-between gap-3">
        <dt className="text-muted">Cart subtotal</dt>
        <dd className="text-ink tabular-nums">
          <Amount minor={cart.subtotal_amount} />
        </dd>
      </div>
      <div className="flex items-baseline justify-between gap-3">
        <dt className="font-semibold text-ink">Cart total, with tax</dt>
        <dd className="text-base font-bold text-ink tabular-nums">
          <Amount minor={cart.total_amount} />
        </dd>
      </div>
    </dl>
  );
}
